import { useEffect, useState } from "react";
import { Search, Pin, Users, Star, Plus, Hash, FolderKanban, ImageIcon, ArrowLeft, AtSign } from "lucide-react";
import { api } from "@/lib/api";
import { avatarTone, isOnline, timeLabel } from "./useChat";
import { usePhotoDirectory, Avatar3D } from "../UserImage";

const FILTERS = [["all", "All"], ["unread", "Unread"], ["mentions", "Mentions"], ["dm", "People"], ["group", "Groups"], ["project", "Projects"]];
const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();

export function Avatar({ name, type, online, size = "h-11 w-11" }) {
  const Icon = type === "group" ? Users : type === "project" ? FolderKanban : type === "channel" ? Hash : null;
  const photo = usePhotoDirectory()[(name || "").trim().toLowerCase()];
  if (!Icon && photo?.avatar_at) return (
    <span className={`relative flex ${size} shrink-0`}>
      <span className="h-full w-full [&>span]:!h-full [&>span]:!w-full"><Avatar3D user={photo} size={44} /></span>
      {online && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />}
    </span>
  );
  return (
    <span className={`relative flex ${size} shrink-0 items-center justify-center rounded-full text-sm font-bold ${avatarTone(name)}`}>
      {Icon ? <Icon className="h-5 w-5" /> : (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
      {online && <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white bg-emerald-500" />}
    </span>
  );
}

function MentionRows({ q, nameOf, onOpenMsg }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/chat/mentions").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <div className="p-6 text-center text-sm text-zinc-400">Loading…</div>;
  const list = rows.filter((m) => !q || `${m.text} ${m.author}`.toLowerCase().includes(q.toLowerCase()));
  if (!list.length) return <div data-testid="chat-mentions-empty" className="p-6 text-center text-sm text-zinc-400">No one has tagged you yet. When someone types @ and picks your name, it shows here.</div>;
  return list.map((m, i) => (
    <button key={m.id} data-testid={`chat-mention-row-${i}`} onClick={() => onOpenMsg(m)} className="flex w-full items-start gap-3 border-b border-zinc-100 px-3 py-2.5 text-left transition-colors hover:bg-zinc-50">
      <Avatar name={m.author} size="h-10 w-10" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2 text-xs"><span className="truncate"><b className="text-zinc-900">{m.author}</b> <span className="text-zinc-500">in {nameOf(m.channel)}</span></span><span className="shrink-0 text-[11px] text-zinc-400">{timeLabel(m.created_at)}</span></div>
        <div className="mt-0.5 line-clamp-2 text-[13px] text-zinc-600"><AtSign className="mr-1 inline h-3 w-3 text-[#15966B]" />{m.text}</div>
      </div>
    </button>
  ));
}

export function ChatList({ convs, active, onOpen, onNewGroup, onStarred, onClose, typing, nameOf, onOpenMsg }) {
  const [q, setQ] = useState("");
  const [f, setF] = useState("all");
  const rows = convs.filter((c) => (!q || c.name.toLowerCase().includes(q.toLowerCase())) && (f === "all" || (f === "unread" ? c.unread > 0 : c.type === f)));
  return (
    <aside data-testid="chat-list" className="flex h-full w-full flex-col border-r border-zinc-200 bg-white md:w-[360px] md:shrink-0">
      <div className="flex items-center justify-between gap-2 bg-[#F6F1E6] px-4 py-3">
        <div className="flex items-center gap-2">
          {onClose && <button data-testid="chat-exit-fullscreen" title="Back to CRM" onClick={onClose} className="rounded-full p-2 text-zinc-600 transition-colors hover:bg-black/5"><ArrowLeft className="h-5 w-5" /></button>}
          <span className="font-display text-lg font-bold text-zinc-900">Chats</span>
        </div>
        <div className="flex gap-1">
          <button data-testid="chat-starred-button" title="Starred messages" onClick={onStarred} className="rounded-full p-2 text-zinc-600 transition-colors hover:bg-black/5"><Star className="h-5 w-5" /></button>
          <button data-testid="chat-new-group-button" title="New group" onClick={onNewGroup} className="rounded-full p-2 text-zinc-600 transition-colors hover:bg-black/5"><Plus className="h-5 w-5" /></button>
        </div>
      </div>
      <div className="px-3 pb-2 pt-3">
        <div className="flex items-center gap-2 rounded-lg bg-zinc-100 px-3">
          <Search className="h-4 w-4 text-zinc-400" />
          <input data-testid="chat-list-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search or start a new chat" className="h-9 flex-1 bg-transparent text-sm outline-none" />
        </div>
        <div className="thin-scroll mt-2 flex gap-1.5 overflow-x-auto pb-1">
          {FILTERS.map(([k, l]) => (
            <button key={k} data-testid={`chat-filter-${k}`} onClick={() => setF(k)}
              className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${f === k ? "bg-zinc-900 text-[#E8C877]" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="thin-scroll flex-1 overflow-y-auto">
        {f === "mentions" ? <MentionRows q={q} nameOf={nameOf} onOpenMsg={onOpenMsg} /> : rows.map((c) => (
          <button key={c.key} data-testid={`chat-conv-${slug(c.name)}`} onClick={() => onOpen(c.key)}
            className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${c.key === active ? "bg-[#F6F1E6]" : "hover:bg-zinc-50"}`}>
            <Avatar name={c.name} type={c.type} online={c.type === "dm" && isOnline(c.user)} />
            <div className="min-w-0 flex-1 border-b border-zinc-100 pb-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-[15px] font-semibold text-zinc-900">{c.name}</span>
                <span className={`shrink-0 text-[11px] ${c.unread ? "font-semibold text-[#A9792C]" : "text-zinc-400"}`}>{timeLabel(c.last?.created_at)}</span>
              </div>
              <div className="mt-0.5 flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1 truncate text-[13px] text-zinc-500">
                  {typing[c.key]?.length ? <span className="text-emerald-600">typing…</span> : c.last ? (
                    <>{c.type !== "dm" && <span className="shrink-0">{c.last.author?.split(" ")[0]}:</span>}{c.last.attachment && <ImageIcon className="h-3.5 w-3.5 shrink-0" />}<span className="truncate">{c.last.text || "Attachment"}</span></>
                  ) : <span className="italic text-zinc-400">{c.sub}</span>}
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {c.pinned && <Pin data-testid={`chat-conv-pinned-${slug(c.name)}`} className="h-3.5 w-3.5 rotate-45 text-zinc-400" />}
                  {c.unread > 0 && <span data-testid={`chat-conv-unread-${slug(c.name)}`} className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#A9792C] px-1.5 text-[11px] font-bold text-white">{c.unread}</span>}
                </span>
              </div>
            </div>
          </button>
        ))}
        {f !== "mentions" && !rows.length && <div className="p-6 text-center text-sm text-zinc-400">No chats found.</div>}
      </div>
    </aside>
  );
}
